import { useEffect, useState } from "react";
import { useToast } from "context/ToastContext";
import { B600 } from "ui-components/CustomButton";
import { Primary } from "ui-components/Buttons";
import { CircularProgress, Dialog, Paper, Typography, Box, IconButton, Grid } from "@mui/material";
import { makeStyles } from '@mui/styles';
import { UpgradeSubscription, CalculateProration, DowngradeSubscription, DeterminePlanChangeType } from "api/subscription";
import { capitalizedSentence, getButtonText, isButtonDisabled } from "utilities/index";
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import theme from 'themes/theme';
import { trackEvent } from "utilities/mixpanel";

const useStyles = makeStyles({
    gradientPaper: {
        // background: `linear-gradient(135deg, rgb(120, 82, 248), rgb(132, 174, 246))`,
        borderRadius: '20px !important',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        backgroundImage:
      'linear-gradient(#fffaf5, #fffaf5), linear-gradient(135deg, #0046ff, #cc5adf)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    border: '2px solid transparent',
    },
    contentSection: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        margin: '0 1rem 1rem 1rem',
        padding: '1.5rem',
        backdropFilter: 'blur(10px)',
        border: '1px solid #EAECF0',
    },
    closeButton: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
        padding: '8px',
        borderRadius: '50%',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
        }
    },
    cancelButton: {
        background: theme?.palette?.customColors.indigo50,
        color: theme?.palette?.customColors?.indigo700,
        padding: "10px 16px",
        borderRadius: '12px',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        height: '52px',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        '&:hover': {
            border: '1px solid rgba(102, 126, 234, 0.4)',
        }
    },
    sparkleIcon: {
        position: 'absolute',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '1.5rem',
    },
    pricingCard: {
        backgroundColor: '#f8f9ff',
        borderRadius: '12px',
        padding: '1.25rem',
        marginTop: '1rem',
        border: '1px solid rgba(102, 126, 234, 0.15)',
    },
    priceRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem',
        '&:last-child': {
            marginBottom: 0
        }
    },
    divider: {
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent)',
        margin: '1rem 0',
    }
});

const UpgradeSubscriptionButton = ({
    subscriptionId,
    newPlanId,
    currentPlanType,
    planTitle,
    newPlanType = "",
    newPlanPrice,
    currentPlanPrice,
    currentPlanEndDate,
    onUpgradeSuccess,
    isFreePlan = false,
    buttonBG,
    currentPlanId,
    startupId,
    newPlanBillingPeriod,
    paymentMethod,
    subscriptionPlanType,
    offerId, // Offer code validated on the pricing page, applied to this plan change
    autoTrigger = false, // Run the click flow on its own, for a plan picked through the URL
    onAutoTriggered
}) => {
    const classes = useStyles();
    const { addToast } = useToast();
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
    const [prorationDetails, setProrationDetails] = useState(null);
    const [openContactUsDialog, setOpenContactUsDialog] = useState(false);
    const [isDowngrade, setIsDowngrade] = useState(false);

    const { mutateAsync: upgradeSubscription } = UpgradeSubscription();
    const { mutateAsync: calculateProration } = CalculateProration();
    const { mutateAsync: downgradeSubscription } = DowngradeSubscription();
    const { mutateAsync: determinePlanChangeTypeAsync } = DeterminePlanChangeType();

    const safeNewPlanType = String(newPlanType || '');
    const safeCurrentPlanType = String(currentPlanType || '');

    const disabledByHelper = isButtonDisabled(
        newPlanPrice,
        currentPlanPrice,
        safeNewPlanType,
        safeCurrentPlanType,
        "",
        ""
    );

    // Determine if this is a downgrade scenario
    const checkIsDowngrade = () => {
        return newPlanPrice <= currentPlanPrice;
    };

    const handleUpgradeClick = async () => {
        if (paymentMethod === "upi" || subscriptionId?.includes("admin-") || newPlanId?.includes("rzp_plan_")) {
            setOpenContactUsDialog(true);
            trackEvent('Upgrade Plan - Contact Us', {
                subscriptionId,
                newPlanId,
                startupId,
                reason: 'UPI payment or admin/manual flow'
            });
            return null;
        }

        if (isFreePlan || disabledByHelper) return;
        const payload = {
            currentPlanId: currentPlanId,
            newPlanId: newPlanId,
            startupId: startupId,
        }
        const response = await determinePlanChangeTypeAsync(payload);
        const isDowngradeScenario = response?.data?.changeType === "downgrade"
        // // console.log(isDowngradeScenario, "isDowngradeScenario")
        setIsDowngrade(isDowngradeScenario);

        if (isDowngradeScenario) {
            // For downgrade, show simple dialog without proration
            setShowDowngradeDialog(true);
            trackEvent('Upgrade Plan - Subscription Change Initiated', {
                action: 'downgrade',
                subscriptionId,
                newPlanId,
                currentPlanId,
                startupId,
                newPlanType,
                currentPlanType: currentPlanType || subscriptionPlanType,
                paymentMethod
            });
        } else {
            // For upgrade, calculate proration
            try {
                const prorationData = await calculateProration({
                    subscriptionId,
                    newPlanId,
                });

                setProrationDetails(prorationData?.data);
                setShowConfirmDialog(true);
                trackEvent('Upgrade Plan - Subscription Change Initiated', {
                    action: 'upgrade',
                    subscriptionId,
                    newPlanId,
                    currentPlanId,
                    startupId,
                    newPlanType,
                    currentPlanType: currentPlanType || subscriptionPlanType,
                    paymentMethod
                });
            } catch (error) {
                addToast(error.response?.data?.message || "Failed to calculate upgrade cost", "error");
            }
        }
    };

    // Same path as a click, so a subscriber arriving with ?plan_type= gets the
    // proration, downgrade or contact-us dialog rather than a fresh checkout.
    useEffect(() => {
        if (!autoTrigger) return;
        if (onAutoTriggered) onAutoTriggered();
        handleUpgradeClick();
    }, [autoTrigger]);

    const handleConfirmUpgrade = async () => {
        setShowConfirmDialog(false);
        setIsUpgrading(true);

        try {
            const payload = {
                subscriptionId,
                planId: newPlanId,
                scheduleChangeAt: "now",
                customer_notify: true,
                ...(offerId ? { offerId } : {}),
            };

            const response = await upgradeSubscription(payload);

            addToast("Subscription upgraded successfully!", "success");
            trackEvent('Upgrade Plan - Subscription Upgrade Confirmed', {
                subscriptionId,
                newPlanId,
                startupId,
                newPlanType,
                currentPlanType: currentPlanType || subscriptionPlanType,
                proration: prorationDetails,
                paymentMethod
            });
            if (onUpgradeSuccess) onUpgradeSuccess(response);
        } catch (error) {
            console.error(error);
            addToast(error.response?.data?.message || "An error occurred", "error");
        } finally {
            setIsUpgrading(false);
        }
    };

    const handleConfirmDowngrade = async () => {
        setShowDowngradeDialog(false);
        setIsUpgrading(true);

        try {
            const payload = {
                subscriptionId,
                planId: newPlanId,
                reason: "User requested downgrade to lower plan",
            };

            const response = await downgradeSubscription(payload);

            addToast("Subscription downgrade scheduled successfully!", "success");
            trackEvent('Upgrade Plan - Subscription Downgrade Confirmed', {
                subscriptionId,
                newPlanId,
                startupId,
                newPlanType,
                currentPlanType: currentPlanType || subscriptionPlanType,
                currentPlanEndDate
            });
            if (onUpgradeSuccess) onUpgradeSuccess(response);
        } catch (error) {
            console.error(error);
            addToast(error.response?.data?.message || "Failed to schedule downgrade", "error");
        } finally {
            setIsUpgrading(false);
        }
    };

    const btnText = isUpgrading
        ? ""
        : getButtonText(
            planTitle,
            safeNewPlanType,
            safeCurrentPlanType,
            isFreePlan,
            newPlanPrice,
            currentPlanPrice,
            "",
            ""
        );

    return (
        <>
            <B600
                className="btn w-100 p-2"
                disabled={isFreePlan || isUpgrading || disabledByHelper}
                onClick={handleUpgradeClick}
                style={{
                    color: "white",
                    border: "none",
                    borderRadius: "9px",
                    display: "flex",
                    textDecoration: "none",
                    alignItems: "center",
                    justifyContent: "center",
                    background: buttonBG,
                    cursor: isFreePlan || isUpgrading || disabledByHelper ? "default" : "pointer",
                    textTransform: "none"
                }}
                sx={{
                    ":disabled": {
                        background: buttonBG,
                        opacity: 0.9
                    },
                    height: { xs: "44px", sm: "50px" },
                    fontSize: {
                        xs: "0.9rem !important",
                        sm: "0.8rem !important",
                        md: "0.9rem !important",
                        lg: "1rem !important",
                    },
                }}
            >
                {isUpgrading ? <CircularProgress size={20} sx={{ color: "white" }} /> : btnText}
            </B600>

            {/* Upgrade Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                PaperProps={{
                    style: {
                        background: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '500px',
                        width: 'calc(100% - 1rem)'
                    }
                }}
            >
                <Paper className={classes.gradientPaper}>
                    <Box className={classes.sparkleIcon} sx={{ top: '20px', left: '20px', display: { xs: "none", sm: "inline" } }}>✨</Box>
                    <Box className={classes.sparkleIcon} sx={{ top: '40px', right: '60px', display: { xs: "none", sm: "inline" } }}>⭐</Box>
                    <Box className={classes.sparkleIcon} sx={{ bottom: '140px', left: '30px', display: { xs: "none", sm: "inline" } }}>✨</Box>
                    <Box className={classes.sparkleIcon} sx={{ bottom: '100px', right: '40px', display: { xs: "none", sm: "inline" } }}>⭐</Box>

                    <IconButton className={classes.closeButton} onClick={() => setShowConfirmDialog(false)}>
                        <CloseIcon width={24} height={24} />
                    </IconButton>

                    <Box sx={{ padding: '2.5rem 1.5rem 1rem 1.5rem', textAlign: 'center' }}>
                        <Typography sx={{
                            color: 'black',
                            fontSize: '1.9rem',
                            fontWeight: 700,
                            marginBottom: { xs: '1rem', sm: "1.5rem" },
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            lineHeight: 1,
                            marginTop: { xs: "1rem", sm: "0rem" }
                        }}>
                            Upgrade Your Plan
                        </Typography>

                        <Typography sx={{
                            color: 'rgba(0, 0, 0, 0.9)',
                            fontSize: '0.95rem',
                            lineHeight: 1.5,
                            maxWidth: '350px',
                            margin: '0 auto',
                        }}>
                            You're upgrading to <>{planTitle}</>
                        </Typography>
                    </Box>

                    <Box className={classes.contentSection} sx={{
                        padding: { xs: "1rem !important", sm: "1.5rem !important" }
                    }} >
                        {prorationDetails && (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sx={{
                                    paddingTop: "0rem !important"
                                }}>
                                    <Box className={classes.pricingCard} >
                                        <Typography sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            // color: '#333',
                                            color: '#555',
                                            marginBottom: '1rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Pricing Breakdown
                                        </Typography>

                                        <Box className={classes.priceRow}>
                                            <Typography sx={{ color: '#333', fontSize: '0.95rem' }}>
                                                New Plan Price ({capitalizedSentence(newPlanBillingPeriod)})
                                            </Typography>
                                            <Typography sx={{ color: '#333', fontSize: '0.95rem', fontWeight: 600 }}>
                                                ₹{prorationDetails.newPlanPrice}
                                            </Typography>
                                        </Box>

                                        <Box className={classes.priceRow}>
                                            <Box>
                                                <Typography sx={{
                                                    // color: '#4caf50',
                                                    fontSize: '0.95rem', fontWeight: 500
                                                }}>
                                                    {/* Credit Applied */}
                                                    Prorated Cost <br /> <span style={{
                                                        color: '#666', fontSize: '0.75rem', marginTop: '2px'
                                                    }}> ({prorationDetails.unusedDays} day{prorationDetails.unusedDays !== 1 ? 's' : ''} remaining)
                                                    </span>
                                                </Typography>
                                                {/* <Typography sx={{ color: '#666', fontSize: '0.75rem', marginTop: '2px' }}>
                                                    {prorationDetails.unusedDays} day{prorationDetails.unusedDays !== 1 ? 's' : ''} remaining
                                                </Typography> */}
                                            </Box>
                                            <Typography sx={{
                                                // color: '#4caf50',
                                                fontSize: '0.95rem', fontWeight: 600
                                            }}>
                                                -₹{prorationDetails.creditAmount}
                                            </Typography>
                                        </Box>

                                        <Box className={classes.divider} />

                                        <Box className={classes.priceRow}>
                                            <Typography sx={{
                                                color: '#333',
                                                fontSize: '1rem',
                                                fontWeight: 700
                                            }}>
                                                Amount to Pay Now
                                            </Typography>
                                            <Typography sx={{
                                                // color: theme.palette.customColors.b300,
                                                color: "#4caf50",
                                                fontSize: '1rem',
                                                fontWeight: 700
                                            }}>
                                                ₹{prorationDetails.amountToPay}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.75rem',
                                        backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(102, 126, 234, 0.15)'
                                    }}>
                                        <Typography sx={{ fontSize: '1.2rem' }}>⚡</Typography>
                                        <Typography sx={{
                                            fontSize: '0.85rem',
                                            color: '#555',
                                            lineHeight: 1.4
                                        }}>
                                            Your new billing cycle starts immediately. Payment will be charged automatically.
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Primary
                                        type="button"
                                        fullWidth
                                        disabled={isUpgrading}
                                        sx={{
                                            padding: { xs: '8px 12px', sm: "16px 24px" },
                                            height: { xs: "40px", sm: "52px" },
                                            fontSize: '1rem'
                                        }}
                                        onClick={handleConfirmUpgrade}
                                    >
                                        {isUpgrading ? (
                                            <CircularProgress size={"1.5rem"} sx={{ color: 'white' }} />
                                        ) : (
                                            "Confirm"
                                        )}
                                    </Primary>
                                </Grid>

                                {/* <Grid item xs={12}>
                                    <Box
                                        className={classes.cancelButton}
                                        component="button"
                                        type="button"
                                        disabled={isUpgrading}
                                        sx={{
                                            width: '100%',
                                            cursor: isUpgrading ? 'default' : 'pointer',
                                            pointerEvents: isUpgrading ? 'none' : 'auto',
                                            padding: { xs: '8px 12px !important', sm: "16px 24px !important" },
                                            height: { xs: "40px !important", sm: "52px !important" }
                                        }}
                                        onClick={() => setShowConfirmDialog(false)}
                                    >
                                        Maybe Later
                                    </Box>
                                </Grid> */}

                                {/* <Grid item xs={12}>
                                    <Typography sx={{
                                        textAlign: 'center',
                                        color: '#666',
                                        fontSize: '0.8rem',
                                        lineHeight: 1.4,
                                        marginTop: '0.25rem'
                                    }}>
                                        🔒 Secure payment • Cancel anytime • Instant activation
                                    </Typography>
                                </Grid> */}
                            </Grid>
                        )}
                    </Box>
                </Paper>
            </Dialog>

            {/* Upgrade Contact Us Dialog */}
            <Dialog
                open={openContactUsDialog}
                onClose={() => setOpenContactUsDialog(false)}
                PaperProps={{
                    style: {
                        background: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '500px',
                        width: 'calc(100% - 1rem)'
                    }
                }}
            >
                <Paper className={classes.gradientPaper}>
                    {/* Decorative Sparkles */}
                    <Box className={classes.sparkleIcon} sx={{ top: '20px', left: '20px', display: { xs: "none", sm: "inline" } }}>✨</Box>
                    <Box className={classes.sparkleIcon} sx={{ top: '40px', right: '60px', display: { xs: "none", sm: "inline" } }}>⭐</Box>
                    <Box className={classes.sparkleIcon} sx={{ bottom: '140px', left: '30px', display: { xs: "none", sm: "inline" } }}>✨</Box>
                    <Box className={classes.sparkleIcon} sx={{ bottom: '100px', right: '40px', display: { xs: "none", sm: "inline" } }}>⭐</Box>

                    {/* Close Button */}
                    <IconButton className={classes.closeButton} onClick={() => setOpenContactUsDialog(false)}>
                        <CloseIcon width={24} height={24} />
                    </IconButton>

                    {/* Header Section */}
                    <Box sx={{ padding: '2.5rem 1.5rem 1rem 1.5rem', textAlign: 'center' }}>
                        <Typography sx={{
                            color: 'black',
                            fontSize: '1.9rem',
                            fontWeight: 700,
                            marginBottom: { xs: '1rem', sm: "1.5rem" },
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            lineHeight: 1,
                            marginTop: { xs: "1rem", sm: "0rem" }
                        }}>
                            Ready to Upgrade?
                        </Typography>

                        <Typography sx={{
                            color: 'rgba(0, 0, 0, 0.9)',
                            fontSize: '0.95rem',
                            lineHeight: 1.5,
                            maxWidth: '350px',
                            margin: '0 auto',
                        }}>
                            Get in touch with our team to upgrade your plan
                        </Typography>
                    </Box>

                    {/* Content Section */}
                    <Box className={classes.contentSection} sx={{
                        padding: { xs: "1rem !important", sm: "1.5rem !important" }
                    }}>
                        <Grid container spacing={2}>
                            {/* Info Card */}
                            <Grid item xs={12}>
                                <Box sx={{
                                    padding: '1.5rem',
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    textAlign: 'center'
                                }}>
                                    {/* <Typography sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#333',
                            marginBottom: '0.75rem'
                        }}>
                            Contact Backrr Support
                        </Typography> */}

                                    <Typography sx={{
                                        fontSize: '0.9rem',
                                        color: '#666',
                                        lineHeight: 1.6,
                                        // marginBottom: '1.25rem'
                                    }}>
                                        Our support team will help you choose the perfect plan and guide you through the upgrade process.
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Info Alert */}
                            <Grid item xs={12}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem',
                                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(102, 126, 234, 0.15)'
                                }}>
                                    <Typography sx={{ fontSize: '1.2rem' }}>💡</Typography>
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        color: '#555',
                                        lineHeight: 1.4
                                    }}>
                                        Our team typically responds within 24 hours to help with plan upgrades.
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Contact Button */}
                            <Grid item xs={12}>
                                <Primary
                                    component="a"
                                    href="mailto:support@backrr.com?subject=Plan Upgrade Request"
                                    fullWidth
                                    sx={{
                                        textDecoration: 'none',
                                        padding: { xs: '8px 12px', sm: "16px 24px" },
                                        height: { xs: "40px", sm: "52px" },
                                        fontSize: '1rem'
                                    }}
                                >
                                    Contact Support
                                </Primary>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Dialog>


            {/* Downgrade Confirmation Dialog */}
            <Dialog
                open={showDowngradeDialog}
                onClose={() => setShowDowngradeDialog(false)}
                PaperProps={{
                    style: {
                        background: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '500px',
                        width: 'calc(100% - 1rem)'
                    }
                }}
            >
                <Paper className={classes.gradientPaper}>
                    <Box className={classes.sparkleIcon} sx={{ top: '20px', left: '20px', display: { xs: "none", sm: "inline" } }}>✨</Box>
                    <Box className={classes.sparkleIcon} sx={{ top: '40px', right: '60px', display: { xs: "none", sm: "inline" } }}>⭐</Box>
                    <Box className={classes.sparkleIcon} sx={{ bottom: '140px', left: '30px', display: { xs: "none", sm: "inline" } }}>✨</Box>
                    <Box className={classes.sparkleIcon} sx={{ bottom: '100px', right: '40px', display: { xs: "none", sm: "inline" } }}>⭐</Box>

                    <IconButton className={classes.closeButton} onClick={() => setShowDowngradeDialog(false)}>
                        <CloseIcon width={24} height={24} />
                    </IconButton>

                    <Box sx={{ padding: '2.5rem 1.5rem 1rem 1.5rem', textAlign: 'center' }}>
                        <Typography sx={{
                            color: 'black',
                            fontSize: '1.9rem',
                            fontWeight: 700,
                            marginBottom: { xs: '1rem', sm: "1.5rem" },
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            marginTop: { xs: "1rem", sm: "0rem" }
                        }}>
                            Change Your Plan
                        </Typography>

                        <Typography sx={{
                            color: 'rgba(0, 0, 0, 0.9)',
                            fontSize: '0.95rem',
                            lineHeight: 1.5,
                            maxWidth: '350px',
                            margin: '0 auto',
                        }}>
                            You're switching to <>{planTitle}</>. Changes take effect at the end of your current billing cycle.
                        </Typography>
                    </Box>

                    <Box className={classes.contentSection} sx={{
                        padding: { xs: "1rem !important", sm: "1.5rem !important" }
                    }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sx={{
                                paddingTop: "0rem !important"
                            }}>
                                <Box className={classes.pricingCard}>
                                    <Typography sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        // color: '#333',
                                        color: '#555',
                                        marginBottom: '1rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Next Plan Details
                                    </Typography>

                                    <Box className={classes.priceRow}>
                                        {/* <Typography sx={{
                                            color: '#333',
                                            fontSize: '1.1rem',
                                            fontWeight: 700
                                        }}>
                                            Price Starting Next Cycle
                                        </Typography>
                                        <Typography sx={{
                                            color: theme.palette.customColors.b300,
                                            fontSize: '1.5rem',
                                            fontWeight: 700
                                        }}>
                                            ₹{newPlanPrice}
                                        </Typography> */}

                                        <Typography sx={{
                                            color: '#333',
                                            fontSize: '1rem',
                                            fontWeight: 700
                                        }}>
                                            Price Starting Next Cycle
                                        </Typography>
                                        <Typography sx={{
                                            // color: theme.palette.customColors.b300,
                                            color: "#4caf50",
                                            fontSize: '1rem',
                                            fontWeight: 700
                                        }}>
                                            ₹{newPlanPrice}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem',
                                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(102, 126, 234, 0.15)'
                                }}>
                                    <Typography sx={{ fontSize: '1.2rem' }}>📅</Typography>
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        color: '#555',
                                        lineHeight: 1.4
                                    }}>
                                        Your current plan remains active until <strong>{currentPlanEndDate}</strong>. The new plan will take effect on your next billing date. No charges today.
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Primary
                                    type="button"
                                    fullWidth
                                    disabled={isUpgrading}
                                    sx={{
                                        padding: { xs: '8px 12px', sm: "16px 24px" },
                                        height: { xs: "40px", sm: "52px" },
                                        fontSize: '1rem'
                                    }}
                                    onClick={handleConfirmDowngrade}
                                >
                                    {isUpgrading ? (
                                        <CircularProgress size={"1.5rem"} sx={{ color: 'white' }} />
                                    ) : (
                                        "Confirm"
                                    )}
                                </Primary>
                            </Grid>

                            {/* <Grid item xs={12}>
                                <Box
                                    className={classes.cancelButton}
                                    component="button"
                                    type="button"
                                    disabled={isUpgrading}
                                    sx={{
                                        width: '100%',
                                        cursor: isUpgrading ? 'default' : 'pointer',
                                        pointerEvents: isUpgrading ? 'none' : 'auto',
                                        padding: { xs: '8px 12px !important', sm: "16px 24px !important" },
                                        height: { xs: "40px !important", sm: "52px !important" }
                                    }}
                                    onClick={() => setShowDowngradeDialog(false)}
                                >
                                    Keep Current Plan
                                </Box>
                            </Grid> */}

                            {/* <Grid item xs={12}>
                                <Typography sx={{
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '0.8rem',
                                    lineHeight: 1.4,
                                    marginTop: '0.25rem'
                                }}>
                                    🔄 Change your mind? You can update anytime before the cycle ends
                                </Typography>
                            </Grid> */}
                        </Grid>
                    </Box>
                </Paper>
            </Dialog>
        </>
    );
};

export default UpgradeSubscriptionButton;
